import { useCallback, useEffect, useState } from 'react'
import { canonicalHashFor, matchRoute, pathFor, type RouteDef, type RouteId } from './routes'

/**
 * The hash router's one hook.
 *
 * 🔴 `hashchange` IS A REAL BROWSER EVENT AND THAT IS THE WHOLE IMPLEMENTATION.
 * Back and forward work because the browser already implements them for hashes;
 * there is no history stack to maintain here, which is most of what a routing
 * library is for and none of what this product needs.
 */
export interface Router {
  route: RouteDef
  navigate: (id: RouteId) => void
  /**
   * ⚠️ `replace` EXISTS FOR ONE CASE: sending a signed-in user off the sign-in
   * screen. Using `navigate` there would put the login form in the back stack,
   * so pressing back after signing in would show it again — and on a shared
   * classroom laptop that reads as "I have been signed out".
   */
  replace: (id: RouteId) => void
}

export function useRoute(): Router {
  const [route, setRoute] = useState<RouteDef>(() => matchRoute(currentHash()))

  useEffect(() => {
    const onChange = (): void => {
      /*
       * 🔴 THE ADDRESS BAR IS CORRECTED TO MATCH THE SCREEN, NOT LEFT LYING.
       *
       * `matchRoute` falls back to the landing page for an unrecognised hash —
       * right, and deliberately so — but the URL used to keep claiming the hash
       * that failed. That is how a one-line path rename presents as a broken
       * screen: the address said `#/teacher/signin` and the marketing page
       * rendered under it, which looks exactly like a mis-wired link, a guard
       * firing, a component failing to mount, or a stale bundle. Now the two
       * cannot disagree, so the same symptom has one meaning.
       *
       * `canonicalHashFor` returns null for a hash that is already right, and
       * for the two that must never be rewritten — an invitation token and the
       * landing page's own section anchors.
       */
      const canonical = canonicalHashFor(currentHash())
      if (canonical !== null && typeof window.history.replaceState === 'function') {
        // Not a navigation: correcting an address is not a place Back returns to.
        window.history.replaceState(null, '', canonical)
      }
      setRoute(matchRoute(currentHash()))
    }
    window.addEventListener('hashchange', onChange)
    /*
     * Re-read on mount as well as on change. The hash may already be set — a
     * bookmark, a refresh, or a link opened cold — and a router that only
     * listened would render the landing page over it.
     */
    onChange()
    return () => {
      window.removeEventListener('hashchange', onChange)
    }
  }, [])

  const navigate = useCallback((id: RouteId) => {
    window.location.hash = pathFor(id)
  }, [])

  const replace = useCallback((id: RouteId) => {
    /*
     * `replaceState` keeps the hash out of the back stack. Guarded because it is
     * unavailable in a `file://` context and in some embedded webviews — a
     * school laptop is not a controlled environment, and falling back to a
     * normal navigation is better than throwing on sign-in.
     */
    const next = pathFor(id)
    if (typeof window.history.replaceState === 'function') {
      window.history.replaceState(null, '', next)
      setRoute(matchRoute(next))
    } else {
      window.location.hash = next
    }
  }, [])

  return { route, navigate, replace }
}

function currentHash(): string {
  return window.location.hash
}
